import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import { useUserContext } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUpdateOwnLanguage } from '@/hooks/useUsers';

/**
 * Menú de idioma fijo (Sidebar): fila con icono de idioma (fa-language) y etiqueta, que despliega
 * la lista de los 7 idiomas soportados. Al elegir uno: cambia i18next al instante, actualiza el perfil
 * local (demoUser) y, si hay una identidad real (MSAL/Teams), persiste PreferredLanguage en el
 * backend para que la próxima sesión cargue el mismo idioma sin importar el dispositivo.
 */
export function LanguageMenu() {
  const { t } = useTranslation();
  const { demoUser, setDemoUser } = useUserContext();
  const currentUser = useCurrentUser();
  const { mutate: persistLanguage } = useUpdateOwnLanguage();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const activeLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === i18n.language)
    ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClickOutside), 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  function selectLanguage(code: string) {
    setOpen(false);
    if (code === i18n.language) return;

    void i18n.changeLanguage(code);
    setDemoUser({ ...demoUser, idioma: code });

    if (currentUser?.id) {
      persistLanguage(
        { id: currentUser.id, preferredLanguage: code },
        {
          onError: (error) => {
            if (import.meta.env.DEV) {
              console.error('LanguageMenu: no se pudo guardar el idioma preferido', error);
            }
          },
        },
      );
    }
  }

  return (
    <div className="lang-menu" ref={containerRef}>
      <button
        type="button"
        className="side-link side-lang-toggle"
        title={t('common.languageMenuTitle')}
        aria-label={t('common.languageMenuTitle')}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
      >
        <i className="fas fa-language lang-menu-icon" aria-hidden="true" />
        <span className="side-link-label">{t('nav.language')}</span>
      </button>

      {open && (
        <div className="lang-menu-popover" role="menu">
          {SUPPORTED_LANGUAGES.map(lang => (
            <button
              key={lang.code}
              type="button"
              role="menuitem"
              className={`lang-menu-option${lang.code === activeLanguage.code ? ' active' : ''}`}
              onClick={() => selectLanguage(lang.code)}
            >
              <span className="lang-menu-option-code">{lang.displayCode}</span>
              <span className="lang-menu-option-native">{lang.native}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
